import { IdentityStatus, type UserInfo } from '@/redux/auth/auth.type';
import { sameOriginPath } from '@/lib/same-origin-path';

/**
 * The one place that decides where a signed-in user belongs in the
 * registration/verification funnel.
 *
 * Login and the OTP page both need this and used to answer it separately: the
 * OTP page assumed every mail-verified user was mid-registration and sent them
 * to the completion link, which only exists while work identity is
 * NOT_INITIATED — so users further along landed on a token-less
 * /complete-profile with no way out. Keep new branches here rather than at a
 * call site.
 */
export const resolveNextAuthStep = (
  user: Pick<
    UserInfo,
    'email' | 'isMailVerified' | 'workEmail' | 'workIdentityVerificationStatus'
  >,
  profileCompletionLink?: string | null,
  verifiedRedirect?: string | null
): string => {
  // A user whose professional-info row is missing has an undefined status here
  // (transformUserInfoData reads it off `userProfessionalInfo[0]`). That means
  // work identity has not been started, which is exactly NOT_INITIATED —
  // without the default it fell past every branch to the status screen and
  // rendered `?status=undefined`.
  const status =
    user?.workIdentityVerificationStatus ?? IdentityStatus.NOT_INITIATED;
  const email = encodeURIComponent(user?.email ?? '');

  if (
    status === IdentityStatus.VERIFIED ||
    status === IdentityStatus.MANUAL_VERIFIED
  ) {
    return sameOriginPath(verifiedRedirect) || '/';
  }

  // Checked ahead of the remaining statuses rather than inside NOT_INITIATED:
  // an unconfirmed private address is the blocking step whatever the work
  // identity says, and the CTA relies on this holding for every state.
  if (!user?.isMailVerified) {
    return `/otp-verify?email=${email}&emailType=PERSONAL`;
  }

  if (status === IdentityStatus.NOT_INITIATED) {
    // A missing link is recoverable: /complete-profile asks for a fresh one
    // when it is opened without a token by a signed-in user.
    return profileCompletionLink || '/complete-profile';
  }

  if (status === IdentityStatus.PENDING) {
    return user?.workEmail
      ? `/otp-verify?email=${encodeURIComponent(user.workEmail)}&emailType=WORK`
      : `/verify-pending?isDocUpload=true&utm_source=doc&utm_content=success`;
  }

  return `/identity-verify?status=${status}&email=${encodeURIComponent(
    user?.workEmail || user?.email || ''
  )}&emailType=${user?.workEmail ? 'WORK' : 'PERSONAL'}`;
};
